import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Platform, ScrollView, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Button, iosVerticalScrollContentContainerStyle } from '@fhs/ui';
import { useIsDesktopBp } from '@fhs-legacy/frontend/src/hooks/breakpoints';
import { useAuthContext } from '@fhs-legacy/frontend/src/state/auth';

import { useDeliveryAddressValidation } from '../../hooks/use-delivery-address-validation';
import { isKeyboardFocus } from '../../utils/is-keyboard-focus';

import { DeliveryAddressField } from './delivery-address-field';
import { useDeliveryPanelMqStyles } from './delivery-panel.styles';
import { DeliveryPoweredBy } from './delivery-powered-by';
import { DeliveryTermsNotice } from './delivery-terms-notice';
import { NoDeliveryRestaurantsModal } from './no-delivery-restaurants-modal';
import { SavedAddressesSection } from './saved-addresses-section';
import { DeliveryAddressFieldHandle, DeliveryPanelProps, DeliveryValidationStatus } from './types';

const isWeb = Platform.OS === 'web';

/**
 * Closing the sheet hands focus back to whatever opened it on web, and iOS won't raise the
 * keyboard for a field while a modal is still dismissing, so refocusing the address field has to
 * wait both out.
 */
const REFOCUS_AFTER_SHEET_CLOSE_MS = 300;

/** The design sits the button 8 above the home indicator, and keeps an even 16 without one. */
const FOOTER_MIN_PADDING_BOTTOM = 16;
const FOOTER_GAP_ABOVE_HOME_INDICATOR = 8;

/**
 * Delivery side of the locator. The guest enters an address instead of picking a store, and
 * Deliver Here checks that a restaurant can reach it before continuing to the menu.
 */
export const DeliveryPanel: React.VFC<DeliveryPanelProps> = ({
  selection,
  onSelectionChange,
  onSwitchToPickup,
}) => {
  const styles = useDeliveryPanelMqStyles();
  const isDesktop = useIsDesktopBp();
  const { bottom: bottomInset } = useSafeAreaInsets();
  const { isAuthenticated } = useAuthContext();
  const { status, validate, reset, dialogs } = useDeliveryAddressValidation();

  const fieldRef = useRef<DeliveryAddressFieldHandle>(null);
  const deliverHereRef = useRef<View>(null);
  const refocusTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const previousSelectionRef = useRef(selection);

  const isPending = status === DeliveryValidationStatus.PENDING;

  // Buttons show focus the way they show a press, so the focus handed to Deliver Here below would
  // leave it dark red after a mouse pick. Only keyboard focus darkens it, and so does a press,
  // which on web focuses it too.
  const [isDeliverHereKeyboardFocused, setIsDeliverHereKeyboardFocused] = useState(true);
  const [isDeliverHerePressed, setIsDeliverHerePressed] = useState(false);

  useEffect(() => () => clearTimeout(refocusTimeoutRef.current), []);

  // Web a11y: once an address resolves, Deliver Here is the next step, so focus moves there for
  // keyboard and screen-reader users. Only a newly picked address does this — not the one the
  // panel remounts with when the guest comes back from Pickup.
  useEffect(() => {
    const previousSelection = previousSelectionRef.current;
    previousSelectionRef.current = selection;

    if (!isWeb || !selection || selection === previousSelection) {
      return;
    }

    const frameId = requestAnimationFrame(() => {
      const element = deliverHereRef.current as unknown as { focus?: () => void } | null;
      element?.focus?.();
    });

    return () => cancelAnimationFrame(frameId);
  }, [selection]);

  const onDeliverHere = useCallback(() => {
    if (!selection || isPending) {
      return;
    }

    validate(selection);
  }, [isPending, selection, validate]);

  const onModalSwitchToPickup = useCallback(() => {
    reset();
    onSwitchToPickup();
  }, [onSwitchToPickup, reset]);

  // Clearing the field clears the selection with it, so the guest starts over on a new address.
  const onChangeAddress = useCallback(() => {
    reset();
    fieldRef.current?.clear();
    clearTimeout(refocusTimeoutRef.current);
    refocusTimeoutRef.current = setTimeout(
      () => fieldRef.current?.focus(),
      REFOCUS_AFTER_SHEET_CLOSE_MS
    );
  }, [reset]);

  return (
    <View style={styles.root} testID="store-locator-delivery-panel">
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={[styles.scrollContent, iosVerticalScrollContentContainerStyle]}
        // On native a tap on a suggestion would otherwise only put the keyboard away.
        keyboardShouldPersistTaps="handled"
      >
        {/* Inside the scroll content rather than above it: Android only delivers touches inside
            every ancestor's bounds, so the suggestions must land within a parent that spans the
            saved addresses they float over. */}
        <View style={styles.addressBlock}>
          <View style={styles.addressField}>
            <DeliveryPoweredBy />
            <DeliveryAddressField
              ref={fieldRef}
              value={selection}
              onChange={onSelectionChange}
              disabled={isPending}
            />
          </View>
          <DeliveryTermsNotice />
        </View>

        <SavedAddressesSection isAuthenticated={isAuthenticated} />
      </ScrollView>

      <View
        style={[
          styles.footer,
          !isDesktop && {
            paddingBottom: Math.max(
              FOOTER_MIN_PADDING_BOTTOM,
              bottomInset + FOOTER_GAP_ABOVE_HOME_INDICATOR
            ),
          },
        ]}
      >
        <Button
          ref={deliverHereRef}
          size="xl"
          testID="store-locator-delivery-deliver-here"
          dd-action-name="store locator deliver here"
          // Validating greys the button out as well as spinning, like the design's pending state.
          disabled={!selection || isPending}
          loading={isPending}
          focusedStyle={
            isDeliverHereKeyboardFocused || isDeliverHerePressed
              ? undefined
              : styles.deliverHereAtRest
          }
          onFocus={event => setIsDeliverHereKeyboardFocused(isKeyboardFocus(event.target))}
          onPressIn={() => setIsDeliverHerePressed(true)}
          onPressOut={() => setIsDeliverHerePressed(false)}
          onPress={onDeliverHere}
        >
          <Button.Text>Deliver Here</Button.Text>
        </Button>
      </View>

      <NoDeliveryRestaurantsModal
        isVisible={status === DeliveryValidationStatus.UNAVAILABLE}
        address={selection}
        onSwitchToPickup={onModalSwitchToPickup}
        onChangeAddress={onChangeAddress}
        onClose={reset}
      />
      {dialogs}
    </View>
  );
};
